import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../components/common/ProtectedRoute';
import { WikiLayout } from '../components/layout/WikiLayout';
import { ChunkReloadFallback, PageFallback } from './routeFallbacks';

// Lazy-loaded pages (code-split por ruta).
// lazyRetry: si el chunk 404ea (deploy nuevo invalidó los hashes y el
// index.html viejo sigue en memoria del tab), recarga la página UNA vez
// para traer el index.html fresco con los hashes actuales.
const LAZY_RELOAD_KEY = 'wiki:chunk-reload';

// Nunca se renderiza: la página se está recargando.
function lazyRetry(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem(LAZY_RELOAD_KEY);
      return mod;
    } catch (err) {
      if (!sessionStorage.getItem(LAZY_RELOAD_KEY)) {
        sessionStorage.setItem(LAZY_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return { default: ChunkReloadFallback };
      }
      throw err;
    }
  });
}

const LoginPage = lazyRetry(() => import('../features/auth/login/pages/LoginPage').then(m => ({ default: m.LoginPage })));
const DashboardPage = lazyRetry(() => import('../features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const ProfilePage = lazyRetry(() => import('../features/profile/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const DepartmentSelectPage = lazyRetry(() => import('../features/department-select/pages/DepartmentSelectPage').then(m => ({ default: m.DepartmentSelectPage })));
const AdminPage = lazyRetry(() => import('../features/admin/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const InfraDashboard = lazyRetry(() => import('../features/infra/pages/InfraDashboard').then(m => ({ default: m.InfraDashboard })));
const DocumentViewPage = lazyRetry(() => import('../features/documents/view/pages/DocumentViewPage').then(m => ({ default: m.DocumentViewPage })));
const DocumentNewPage = lazyRetry(() => import('../features/documents/new/pages/DocumentNewPage').then(m => ({ default: m.DocumentNewPage })));
const DocumentEditPage = lazyRetry(() => import('../features/documents/edit/pages/DocumentEditPage').then(m => ({ default: m.DocumentEditPage })));

const withSuspense = (element: React.ReactNode) => (
  <Suspense fallback={<PageFallback />}>{element}</Suspense>
);

export const router = createBrowserRouter([
  { path: '/login', element: withSuspense(<LoginPage />) },
  {
    path: '/departments',
    element: <ProtectedRoute>{withSuspense(<DepartmentSelectPage />)}</ProtectedRoute>,
  },
  {
    path: '/infra',
    element: <ProtectedRoute><WikiLayout /></ProtectedRoute>,
    children: [{ index: true, element: withSuspense(<InfraDashboard />) }],
  },
  {
    path: '/admin',
    element: <ProtectedRoute><WikiLayout /></ProtectedRoute>,
    children: [{ index: true, element: withSuspense(<AdminPage />) }],
  },
  {
    path: '/profile',
    element: <ProtectedRoute><WikiLayout /></ProtectedRoute>,
    children: [{ index: true, element: withSuspense(<ProfilePage />) }],
  },
  {
    path: '/',
    element: <ProtectedRoute><WikiLayout /></ProtectedRoute>,
    children: [
      { index: true, element: withSuspense(<DashboardPage />) },
      { path: 'documents/nuevo', element: withSuspense(<DocumentNewPage />) },
      { path: 'documents/:id', element: withSuspense(<DocumentViewPage />) },
      { path: 'documents/:id/editar', element: withSuspense(<DocumentEditPage />) },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
